<template>
<div :class="(config.layout === 'desktop') ? 'with-breadcrumb' : 'without-breadcrumb'">

    <page-loading v-if="!data_loaded" />

    <div v-else class="container-fluid">
    
        <div class="row group" :class="{ 'inactive' : group.status === 0 }">

            <div v-if="group.status === 0" class="col-12">
                <div class="alert alert-primary">
                    This group has been marked inactive. All records are retained, but will not show in search results, maps, reports, etc.
                    <a href="#" class="alert-link" v-if="group.can.update" @click="setActive()">Mark as Active</a>.
                </div>
            </div>

            <div class="col-12 col-md-7">        
                <provider-tasks                
                    :tasks="group.tasks_incomplete"
                    :morphable_id="group.id"
                    morphable_type="App\Group"
                    :display_name="group.display_name"
                    test-data="groupTasks"
                />

                <!-- Group Details Card -->
                <div class="card" :class="{'pending' : pending}" id="profile">
                    <img v-if="pending" class="center-me" src="/images/loading-color.svg">

                    <div class="profile-header d-flex align-items-end rounded-top text-white text-3xl">

                        <span class="profile-name flex-grow-1 font-weight-bold mr-2 mb-2 leading-none" data-test="pageTitle">
                        	{{ group.name }}
                        </span>

                        <div class="drop-down drop-down-show dropleft mr-35" style="z-index:10">
                            <a href="#" role="button" id="groupDropDown" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false" data-test="contextMenu">
                                <i class="far fa-ellipsis-h text-xl text-white"></i>
                            </a>
                            <div class="dropdown-menu py-0" aria-labelledby="groupDropDown">
                                <span class="dropdown-item" @click="setFollowUp()" data-test="tagGroup">Tag for follow-up</span>
                                <template v-if="group.can.update">
                                    <span class="dropdown-item" @click="startEditing()" data-test="editGroup">Edit Group</span>
                                </template>
                                <template v-if="group.can.delete">
                                    <span v-if="group.status === 0" class="dropdown-item" @click="setActive()" data-test="markGroupActive">Mark as Active</span>
                                    <span v-else class="dropdown-item" @click="setInactive()" data-test="markGroupInactive">Mark as inactive</span>
                                    <span class="dropdown-item" @click="deleteGroup()" data-test="deleteGroup">Delete</span>
                                </template>
                            </div>
                        </div>

                        <div class="profile-image rounded-top"></div>
                    </div>

                    <div class="px-35 pt-2 pb-35">
                        <div class="mb-3" :class="($root.$mq === 'xs' || $root.$mq === 'sm') && editing ? '' : 'd-flex'">
                            
                            <template v-if="editing">
                                <avatar-field 
                                    class="profile-avatar" style="position: relative;"
                                    ref="image_field"
                                    :default_url="group.photo_url"
                                    @avatar_changed="changeLocalAvatar($event)"
                                /> 
                                <div class="flex-grow-1">

                                    <div class="d-flex mt-2 mb-1 align-items-center">
                                        <i class="fal fa-users-medical text-gray-500 mr-1 fa-fw"></i>
                                        <input v-model="form.name" class="form-control" placeholder="Group name" :class="{ 'is-invalid' : form.errors.has('name')}" required data-test="groupName">
                                        <has-error :form="form" field="name"></has-error>
                                    </div>

                                    <div class="d-flex mb-3 align-items-center">
                                        <i class="fal fa-address-book text-gray-500 mr-1 fa-fw"></i>
                                        <input v-model="form.contact_first_name" class="w-50 form-control mr-1" placeholder="Contact first name" :class="{ 'is-invalid' : form.errors.has('contact_first_name')}" required data-test="groupContactFirstName">
                                        <input v-model="form.contact_last_name" class="w-50 form-control" placeholder="Contact last name" :class="{ 'is-invalid' : form.errors.has('contact_last_name')}" required data-test="groupContactLastName">
                                        <has-error :form="form" field="contact_first_name"></has-error>
                                        <has-error :form="form" field="contact_last_name"></has-error>
                                    </div>

                                    <div class="d-flex align-items-center mb-2 specialties">
                                        <i class="fal fa-stethoscope text-gray-500 mr-1 fa-fw"></i>                
                                        <v-select                                
                                            multiple
                                            label="label"                                
                                            :options="options.specialties"
                                            :reduce="label => label.id"
                                            v-model="form.group_specialty"
                                            data-test="groupSpecs"
                                        />
                                    </div>

                                    <div class="mb-2">
                                        <div v-for="email in form.emails" class="d-flex align-items-center">
                                            <i class="fal fa-envelope text-gray-500 mr-2 fa-fw" v-tooltip.bottom="'Email'"></i>   
                                            <div class="w-100 d-flex">
                                                <input v-if="email.verified_at" v-model="email.email" type="email" class="form-control mb-2" disabled v-tooltip.bottom="'Verified Emails cannot be edited, but can be deleted'" >
                                                <input v-else v-model="email.email" type="email" class="form-control mb-2" placeholder="Email" data-test="groupEmail" :data-lpignore="true" >
                                                
                                                <span
                                                    v-if="!email.primary"
                                                    @click="makeEmailPrimary(email)"
                                                    v-tooltip.bottom="'Make Primary'"
                                                    class="btn btn-sm text-gray-500 cursor-pointer leading-none float-right pt-2"
                                                    data-test="makePrimary" >
                                                    <i class="far fa-star text-base"></i>
                                                </span>
                                                <span v-else
                                                    v-tooltip.bottom="'Is Primary'"
                                                    class="btn btn-sm text-gray-500 leading-none float-right pt-2"
                                                    style="cursor:default;"
                                                    >
                                                    <i class="fas fa-star text-base"></i>
                                                </span> 
                                                <span
                                                    @click="deleteEmail(email)"
                                                    v-tooltip.bottom="'Delete Email'"
                                                    class="btn btn-sm text-gray-300 cursor-pointer leading-none float-right pt-2"
                                                    data-test="deleteEmail" >
                                                    <i class="fas fa-times-circle"></i>
                                                </span>
                                            </div>
                                        </div>   
                                        <div style="height: 25px;">
                                            <span
                                                @click="addEmail()"
                                                v-tooltip.bottom="'Add Email'"
                                                class="btn btn-sm btn-ghost-primary text-gray-500 cursor-pointer leading-none float-right"
                                                data-test="addEmailButton" >
                                                <i class="far fa-plus-circle text-base" />
                                            </span> 
                                        </div>
                                    </div>

                                    <div class="mb-2 d-flex align-items-center">              
                                        <i class="fal fa-id-card-alt text-gray-500 mr-1 fa-fw"v-tooltip.bottom="'NPI Number'"></i>
                                        <input v-model="form.npi" class="form-control" placeholder="NPI number" data-test="groupNpi">
                                    </div>

                                    <div v-if="editing" class="form-group text-right">
                                        <button class="btn btn-ghost-secondary" @click="stopEditing()">Cancel</button>
                                        <button class="btn btn-primary" @click="updateGroup()" data-test="saveGroupButton">Save</button>
                                    </div>
                                </div> 
                            </template>

                            <template v-else>
                                <div class="profile-avatar" style="position: relative;">
                                    <img v-if="image_updating" src="/images/loading-color.svg" class="center-me" style="margin-left: -8px; margin-top: -23px;">
                                    <div class="rounded-circle circle-cropped avatar-xl mr-3 bg-white">
                                        <img class="img-fluid" :class="image_updating ? 'inactive' : ''" :src="group.photo_url" alt="Avatar" >
                                    </div>                    
                                </div>

                                <div class="flex-grow-1">
                                    <span
                                        v-for="specialty in group.specialties"
                                        class="badge text-base bg-teal-200 text-teal-700 mb-2 mr-1"
                                        v-html="specialty.label"
                                        data-test="groupSpecsList"
                                    />

                                    <div v-if="group.contact_name" class="mb-2 d-flex align-items-baseline">
                                        <i class="fal fa-address-book text-gray-500 mr-2" v-tooltip.bottom="'Contact name'"></i>
                                        {{ group.contact_name }}</a>
                                    </div> 
   
                                    <div v-if="group.emails.length" class="mb-3">
                                        <div v-for="email in group.emails" class="">
                                            <a :href="'mailto:' + email.email">{{ email.email }}</a>
                                            <span v-if="email.primary" v-tooltip.top="'Primary Email'"><i class="fas fa-star text-base"></i></span>
                                            <span v-if="email.verified_at" v-tooltip.top="'Verified at ' + email.verified_at"><i class="fas fa-badge-check"></i></span> 
                                        </div>
                                    </div> 

                                </div>
                              
                            </template>

        	            </div>

        				<div class="d-flex border-top" style="margin:0 -1.25rem;">
        					<div class="flex-fill text-center mt-3 border-right">
        						<span class="text-gray-500 text-sm">Physicians</span>
        						<div class="d-flex justify-content-center align-items-baseline">
        							<div class="text-3xl mr-2">
        								{{ group.physicians.length }}
        							</div>
        							<baby-progress-bar :level=rankProgressBar />
        						</div>
        					</div>
        					<div class="flex-fill text-center mt-3 border-right">
        						<span class="text-gray-500 text-sm">Check-Ins</span>
        						<div class="d-flex justify-content-center align-items-baseline">
        							<div class="text-3xl mr-2">
        								{{ group.checkins.length }}
        							</div>
        							<baby-progress-bar :level=rankCheckins />
        						</div>
        					</div>
        					<div class="flex-fill text-center mt-3">
        						<span class="text-gray-500 text-sm">Last Check-in</span>
        						<div class="d-flex justify-content-center align-items-baseline">
        							<div class="text-3xl mr-2">
        								{{ sinceLastCheckin }}
        							</div>
        							<baby-progress-bar level=3 />
        						</div>
        					</div>
        				</div>
                    </div>

                    <div class="card-footer py-2 text-gray-500 d-flex align-items-center">
                        <template v-if="group.user">
                            Belongs to <div class="rounded-circle circle-cropped avatar-xs mx-2"><img class="img-fluid" :src="group.user.photo_url"></div>
                            {{ group.user.display_name }}
                        </template>
                        <em v-else>Belongs to organization</em>
                    </div>                
                </div>

                <locations-manager
                    id="locations"
                    :locationable_id="group.id"
                    locationable_type="Group"
                    :can_update_locations="group.can.update"
                    :locations="group.locations"
                    data-test="groupLocations"
                />

                <div class="card" id="physicians">
                    <div class="card-header d-flex justify-content-between align-items-center">
                        Physicians
                        <span
                            v-if="group.can.update"
                            @click="showEditingPhysicians(true)"
                            v-tooltip.bottom="'Add Physicians'"
                            class="btn btn-sm btn-ghost-primary text-gray-500 cursor-pointer leading-none"
                            data-test="addPhysiciansToGroupButton" >
                            <i class="far fa-plus-circle text-base" />
                        </span>
                    </div>
                    <div v-if="physiciansState" class="px-35 py-25 border-bottom">
                        <v-select
                            placeholder="Add physicians to group"
                            multiple
                            label="display_name"
                            :options="nonGroupPhysicians"
                            :reduce="display_name => display_name.morphable_id"
                            v-model="addPhysicianIds"
                            data-test="newGroupPhysicians"
                            class="mb-2"
                        />  
                        <div class="form-group text-right">
                            <button type="button" class="btn btn-ghost-secondary" @click="showEditingPhysicians(false)">Cancel</button>
                            <button type="button" class="btn btn-primary" @click="savePhysicians()" data-test="saveGroupPhysiciansButton">Save</button>  
                        </div>                         
                    </div>

                    <ul class="list-group list-group-flush">
                        <li v-for="physician in group.physicians" class="d-flex list-group-item drop-down-hover">
                            <div class="rounded-circle circle-cropped avatar-sm mr-2 font-weight-bold text-sm text-center">
                                <img v-if="physician.photo_path" :src="physician.photo_url" class="img-fluid">
                                <div v-else class="bg-gray-200 text-gray-600 border">{{ physician.initials }}</div>
                            </div>
                            <div class="flex-grow-1">                        
                                <router-link :to="{ name: 'physicians.show', params:{ id:physician.id } }">{{ physician.display_name }}</router-link>
                                <div>
                                    <span
                                        v-for="specialty in physician.specialties"
                                        class="badge bg-teal-200 text-teal-700 mr-1"
                                        v-html="specialty.label"
                                    />
                                </div> 
                            </div>
                            <div class="drop-down dropleft">
                                <a href="#" role="button" id="dropdownMenuLink" data-toggle="dropdown">
                                    <i class="far fa-ellipsis-h text-xl"></i>
                                </a>
                                <div class="dropdown-menu py-0" aria-labelledby="dropdownMenuLink">
                                    <span class="dropdown-item" @click="buildCheckinModal(physician.id, physician.display_name)" data-toggle="modal" data-target="#myModal">Add Check-in</span>
                                    <router-link :to="{ name: 'physicians.show', params:{ id:physician.id } }" class="dropdown-item">View Profile</router-link>
                                    <span class="dropdown-item" @click="removePhysicians(physician.id)">Remove from Group</span>                                    
                                </div>
                            </div>                                                 
                        </li>
                    </ul>
                    <div v-if="group.physicians.length == 0 && !physiciansState" class="text-center text-lg text-gray-500 p-3">
                        No physicians in this group
                    </div>
                </div>

                <notes-manager
                    id="notes"
                    :noteable_id="group.id"
                    noteable_type="Group"
                    :can_update="group.can.update"
                    :notes="group.notes"
                    data-test="groupNotes"
                />

            </div>

            <div class="col-12 col-md-5 mb-3 pb-5 pb-md-0" id="checkins">
                <physician-checkins
                    :morphable_id="group.id"
                    morphable_type="App\Group"
                    :display_name="group.display_name"
                    :photo_url="group.photo_url"
                    :checkins="group.checkins"
                    :can_create_checkin="group.status ? perms.can_create_checkin : false"
                />
            </div>

        </div>

    </div>

    <section id="bottom-menu" class="d-md-none navbar fixed-bottom bg-white text-center text-sm border-top pt-1 pb-0" style="z-index:1010">
        <a class="btn btn-ghost btn-sm text-gray-700 text-sm" href="#" v-scroll-to="'#profile'">
            <i class="text-secondary fal fa-user"></i><br>
            Profile
        </a>
        <a class="btn btn-ghost btn-sm text-gray-700 text-sm" href="#" v-scroll-to="'#locations'">
            <i class="text-secondary fal fa-map-marker-alt"></i><br>
            Locations
        </a>
        <a class="btn btn-ghost btn-sm text-gray-700 text-sm" href="#" v-scroll-to="'#physicians'">
            <i class="text-secondary fal fa-chart-bar"></i><br>
            Physicians
        </a>
        <a class="btn btn-ghost btn-sm text-gray-700 text-sm" href="#" v-scroll-to="'#notes'">
            <i class="text-secondary fal fa-sticky-note"></i><br>
            Notes
        </a>
        <a class="btn btn-ghost btn-sm text-gray-700 text-sm" href="#" v-scroll-to="'#checkins'">
            <i class="text-secondary fal fa-check-circle"></i><br>
            Checkins
        </a>
    </section>
</div>
</template>


<script>
    import { mapGetters, mapState } from 'vuex';
    import BreadcrumbsManager from '../../helpers/BreadcrumbManager';

    import ProviderTasks from '../../components/ProviderTasks';
    import PhysicianCheckins from '../../components/PhysicianCheckins';
    import NotesManager from '../../components/NotesManager';
    import LocationsManager from '../../components/LocationsManager';

    import AvatarField from '../../components/AvatarField';
    import BabyProgressBar from '../../components/BabyProgressBar';
    import { Form, HasError, AlertError, AlertErrors, AlertSuccess } from 'vform';

    export default {        
        mixins: [BreadcrumbsManager],

        components: {
            ProviderTasks,
            NotesManager,
            PhysicianCheckins,
            LocationsManager,

            AvatarField,
            BabyProgressBar,
            HasError,
            AlertError,
            AlertErrors,
            AlertSuccess,
            Form
        },

        props: {
            id: {
                type: Number,
                required: true,
            }
        },

        data() {
            return {
                data_loaded: false,
                editing: false,
                pending: false,
                new_avatar: false,
                image_updating: false,
                group: null,
                form: {}, 

                physiciansState: false,
                addPhysicianIds: [],                          
            }
        },

        created() {
        },

        activated() {
            this.fetchGroup();
            this.setBreadcrumbs([
                { text: 'Home', to: { name: 'home' } },
                { text: 'Groups', to: { name: 'groups.list'} },
                { text: '[Group Name]', to: { name: 'groups.show'} },
            ]);
        },

        methods: {
            buildCheckinModal(id, name) {
                let checkin_options = {
                    physician: {
                        physician_id: id,
                        display_name: name,
                    }
                };
                this.$root.$emit('build_checkin_modal', { checkin_options: checkin_options });
            },

            fetchGroup() {
                axios.get('/api/groups/' + this.id ).then(({data}) => {
                    this.group = data;
                    this.data_loaded = true;

                    this.replaceBreadcrumb({
                        find: '[Group Name]',
                        replace: { text: this.group.name }
                    });                    
                });
            },

            removePhysicians(id) {
                axios.put('/api/groups-physicians', { group_id: this.group.id, physician_id: id }).then(({data}) => {
                    Swal.fire({
                        toast: true,
                        position: 'top-end',
                        showConfirmButton: false,
                        timer: 3000,
                        icon: 'success',
                        title: 'Physicians removed',
                    });
                    this.group.physicians.splice(this.group.physicians.findIndex((physician) => physician.id == id), 1);
                });
            },

            savePhysicians() {
                axios.post('/api/groups-physicians', { group_id: this.group.id, physician_ids: this.addPhysicianIds }).then(({data}) => {
                    Swal.fire({
                        toast: true,
                        position: 'top-end',
                        showConfirmButton: false,
                        timer: 3000,
                        icon: 'success',
                        title: 'Physicians added',
                    });
                    this.group.physicians = data;
                    this.showEditingPhysicians(false);
                    this.addPhysicianIds = [];
                });
            },

            startEditing() {                      
                this.editing = true;

                this.form = new Form({
                    name:               this.group.name,
                    contact_first_name: this.group.contact_first_name,
                    contact_last_name:  this.group.contact_last_name,
                    npi:                this.group.npi,
                    emails:             this.group.emails,
                    group_specialty:    this.group.specialties.map(spec => spec.id),
                    status:             this.group.status,
                    client_id:          this.group.client_id,
                    image:          null,
                }); 

                // add 1 email to start if it's empty
                if (this.form.emails.length == 0) {
                    this.addEmail();
                }
            },

            stopEditing() {
                this.editing = false;               
            },

            addEmail() {
                this.form.emails.push({
                    email: '', 
                    primary: (this.form.emails.length == 0) ? 1 : 0,
                });
            },

            makeEmailPrimary(emailObject) {
                for (var i=0; i < this.form.emails.length; i++) {
                    if (this.form.emails[i].email == emailObject.email) {
                        this.form.emails[i].primary = 1;
                        this.form.emails[i].edited = 1;
                    } else if (this.form.emails[i].primary) {
                        this.form.emails[i].primary = 0;
                        this.form.emails[i].edited = 1;
                    }
                }
            },

            deleteEmail(emailObject) {
                var wasPrimary = false;
                for (var i=0; i < this.form.emails.length; i++) {
                    if (this.form.emails[i].email == emailObject.email) {
                        if (this.form.emails[i].primary) {
                            wasPrimary = true;
                        }
                        this.form.emails.splice(i, 1); // delete
                    }
                }
                // if that was the primary email, and there are more, just choose another one
                if (wasPrimary && this.form.emails.length > 0) {
                    this.form.emails[0].primary = 1;
                    this.form.emails[0].edited = 1;
                }

            },            

            showEditingPhysicians(value) {
                this.physiciansState = value;
            },

            updateGroup() {
                this.pending = true;
    	        //file uploads don't work super nice; send avatar afterwards
                if (this.new_avatar) {
                    this.updateGroupImage();
                }

                this.form.put('/api/groups/' + this.group.id).then(({data}) => {
                    this.editing = false;
                    this.group = data;

                    //prevent flashing old image since new image hasn't saved yet
                    if (this.new_avatar) {
                        this.group.photo_url = this.new_avatar;
                    }
                    this.$store.dispatch('updateGroup', data);
                    this.pending = false;
                })
            },

            changeLocalAvatar($event) {
                this.new_avatar = $event;
                this.group.photo_url = this.new_avatar;
            },

            updateGroupImage() {
                const formData = new FormData();
                formData.append('image', this.$refs.image_field.file);

                this.image_updating = true;

                axios.post('/api/groups/' + this.group.id + '/image', formData, {
                    headers: { 
                    	'Content-Type': 'multipart/form-data'
                    }
                })
                .then(({data}) => {
                    this.image_updating = false;
                    this.group.photo_url = data;
                })
            },

            setActive() {
                this.pending = true;
                axios.patch('/api/groups/' + this.group.id, { status: 1 }).then(response => {
                    this.fetchGroup();
                    this.$store.dispatch('addNewGroup', this.group);
                    this.pending = false;
                });
            },

            setInactive() {
                this.pending = true;
                axios.patch('/api/groups/' + this.group.id, { status: 0 }).then(response => {
                    this.group.status = 0;
                    this.$store.dispatch('removeGroups', [this.group.id]);
                    this.pending = false;
                });
            },

            setFollowUp() {
                this.$root.$emit('set_follow_up');
            },

            deleteGroup($event) {
                Swal.fire({
                    title: 'Are you sure?',
                    text: 'Deleting a group also deletes all associated items (check-ins, locations, route visits, etc.). Marking a record inactive is typically preferred over deletion.',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: 'Delete group',
                    cancelButtonText: 'Keep group',
                    animation: false,
                }).then((result) => {
                    if (result.value) {
                        axios.delete('/api/groups/' + this.group.id).then(() => {
                            Swal.fire({
                                toast: true,
                                position: 'top-end',
                                showConfirmButton: false,
                                timer: 3000,
                                icon: 'success',
                                title: 'Group deleted',
                            });                            
                            //need a redirect here
                            this.$router.push({ name: 'groups.list' });
                            this.$store.dispatch('removeGroups', [this.group.id]);
                        })
                    }
                });
            },       
        },
            
        computed: {
            nonGroupPhysicians() {
                return this.allPhysicianIds.filter(physician => !this.group.physicians.some(groupPhys => physician.id == groupPhys.id));
            },

        	rankCheckins() {
        		if (this.group.checkins.length == 0) { return 0; }
        		if (this.group.checkins.length < 2) { return 1; }
        		if (this.group.checkins.length < 4) { return 2; }
        		if (this.group.checkins.length < 7) { return 3; }
        		if (this.group.checkins.length < 10) { return 4; }
        		return 5;
        	},

        	rankProgressBar() {
        		if (!this.group.overall_rank) {
        			return 0;
        		} else if (!this.group.client_metrics) {
                    return 0;
                } else {
        			const percentage = (this.group.client_metrics.count_groups - this.group.overall_rank) / (this.group.client_metrics.count_groups -1);
        			
        			if (percentage < .25) { return 1; }
        			if (percentage < .50) { return 2; }
        			if (percentage < .70) { return 3; }
        			if (percentage < .90) { return 4; }
        			return 5;
        		}
        	},

            sinceLastCheckin() {
            	if (this.group.checkins[0]) {
            		return moment(this.group.checkins[0].created_at).fromNow().split(' ').slice(0,-1).join(' ');
            	}
            	else {
            		return '--';
            	}
            },
            ...mapGetters(['allPhysicianIds']),
            ...mapState(['config', 'options', 'perms'])
        },           
    }
</script>

<style scoped>
    .with-breadcrumb {
        margin-top:70px;
    }

    .without-breadcrumb {
        margin-top:20px;
    }

    .specialties .dropdown {
    	width:100%;
    }

	.button-wrap {
		border-top:1px solid #e4e7ea;
		margin:0 -1rem; }

	.merge-destination {
		background:#e4e7ea;
		box-shadow:inset 7px 0 9px -7px rgba(0,0,0,0.4);
		padding-top:3rem;
	}

</style>